import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Truck, Tag, MapPin, User, ClipboardCheck, LogIn, ArrowLeft, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, fmtPrice } from "@/lib/api";
import { ARAB_COUNTRIES, ARAB_COUNTRY_MAP, validatePhone } from "@/constants/arabCountries";

const CMAP = { PS: "فلسطين", JO: "الأردن", LB: "لبنان", SY: "سوريا" };
const STEPS = ["بياناتك وعنوانك", "الشحن والدفع", "المراجعة والتأكيد"];

export default function Checkout() {
  const { items, subtotal, weight, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", phone: "", email: "", country: "PS", city: "", area: "", street: "", building: "", address: "", note: "", payment_method: "card", shipping_method: "standard", coupon: "" });
  const [customFields, setCustomFields] = useState([]);
  const [customVals, setCustomVals] = useState({});
  const [addresses, setAddresses] = useState([]);
  const [shipping, setShipping] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { if (items.length === 0 && !submitted) navigate("/cart"); }, [items.length, submitted]);
  useEffect(() => { if (user) api.get("/my/addresses").then((r) => setAddresses(r.data)).catch(() => {}); }, [user]);
  useEffect(() => { api.get("/fields", { params: { scope: "orders" } }).then((r) => setCustomFields(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (user && !form.name) setForm((f) => ({ ...f, name: user.name || "", email: user.email || "" }));
  }, [user]);

  const applyAddress = (a) => {
    const code = ARAB_COUNTRY_MAP[a.country] ? a.country : (Object.keys(CMAP).find((k) => CMAP[k] === a.country) || "PS");
    const cc = ARAB_COUNTRY_MAP[code];
    const city = cc && cc.cities.includes(a.city) ? a.city : "";
    setForm((f) => ({ ...f, name: a.name, phone: (a.phone || "").replace(/^\+?\d{1,4}\s*/, "").replace(/\D/g, ""), city, address: a.address, country: code }));
  };
  useEffect(() => {
    if (weight > 0) api.get("/shipping/quote", { params: { weight_grams: weight, country: form.country === "PS" ? "PS" : "INT" } }).then((r) => setShipping(r.data.cost));
  }, [weight, form.country]);

  const applyCoupon = () => {
    if (form.coupon.trim().toUpperCase() === "SUHOOL10") { setDiscount(subtotal * 0.1); toast.success("طُبّق الكوبون: خصم 10%"); }
    else { setDiscount(0); toast.error("كوبون غير صالح"); }
  };

  const total = subtotal + shipping - discount;

  const validStep1 = () => {
    if (!form.name.trim() || !form.phone.trim() || !form.city.trim() || !form.address.trim()) {
      toast.error("الاسم ورقم الهاتف والمحافظة والعنوان التفصيلي مطلوبة"); return false;
    }
    const cc = ARAB_COUNTRY_MAP[form.country];
    if (cc && !cc.cities.includes(form.city)) {
      toast.error("يرجى اختيار محافظة ضمن الدولة المحددة"); return false;
    }
    if (!validatePhone(form.country, form.phone)) {
      toast.error(`رقم هاتف غير صالح لـ${cc?.name || "الدولة"} (${cc?.dial}). يرجى إدخال رقم محلي صحيح`); return false;
    }
    for (const f of customFields) {
      if (f.required && !customVals[f.key]) { toast.error(`الحقل «${f.label}» مطلوب`); return false; }
    }
    return true;
  };

  const next = () => { if (step === 1 && !validStep1()) return; setStep((s) => Math.min(3, s + 1)); window.scrollTo(0, 0); };

  const submit = async () => {
    setSubmitting(true);
    setSubmitted(true);
    const cc = ARAB_COUNTRY_MAP[form.country];
    const fullPhone = `${cc?.dial || ""} ${(form.phone || "").replace(/\D/g, "")}`.trim();
    try {
      const res = await api.post("/orders", {
        ...form,
        phone: fullPhone,
        country: form.country,
        items: items.map((i) => ({ sku: i.sku, name_ar: i.name_ar, price: i.price, qty: i.qty, weight_grams: i.weight_grams })),
        custom_fields: customVals,
      });
      const order = res.data;
      if (form.payment_method === "card") {
        const pay = await api.post("/payments/checkout", { order_no: order.order_no, origin_url: window.location.origin });
        clear();
        window.location.href = pay.data.checkout_url;
      } else {
        clear();
        navigate(`/payment/success?order_no=${order.order_no}&t=${order.qr_token}`);
        toast.success("تم استلام طلبك!");
      }
    } catch (err) {
      toast.error("تعذّر إتمام الطلب. حاول مجدداً.");
      setSubmitting(false);
      setSubmitted(false);
    }
  };

  return (
    <div className="min-h-screen pt-24">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
        <h1 className="mb-6 font-display text-2xl font-bold text-ink sm:text-3xl">إتمام الطلب</h1>

        {/* Stepper */}
        <div className="mb-8 flex items-center gap-1.5 sm:gap-3" data-testid="checkout-steps">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <button type="button" onClick={() => i + 1 < step && setStep(i + 1)}
                className={`flex items-center gap-2 ${i + 1 <= step ? "text-olive-dark" : "text-ink/40"}`}>
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-bold transition-colors ${
                  i + 1 < step ? "border-olive bg-olive text-ivory" : i + 1 === step ? "border-olive bg-olive/10 text-olive" : "border-olive/20"}`}>
                  {i + 1}
                </span>
                <span className={`hidden text-sm font-medium sm:block ${i + 1 === step ? "text-ink" : ""}`}>{s}</span>
              </button>
              {i < STEPS.length - 1 && <span className={`h-px flex-1 ${i + 1 < step ? "bg-olive" : "bg-olive/15"}`} />}
            </React.Fragment>
          ))}
        </div>

        {!user && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gold/40 bg-gold/[0.07] p-4">
            <div className="flex items-center gap-2.5 text-sm text-ink/70">
              <LogIn className="h-5 w-5 text-earth" />
              لديك حساب؟ سجّل دخولك لتعبئة بياناتك تلقائيًا وحفظ فواتيرك. سلتك محفوظة ولن تضيع.
            </div>
            <Link to="/login?redirect=/checkout" data-testid="checkout-login-link"
              className="rounded-full bg-olive px-5 py-2.5 text-sm font-semibold text-ivory">تسجيل الدخول / إنشاء حساب</Link>
          </div>
        )}

        <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
          <div className="space-y-6">
            {step === 1 && (
              <Card title="بيانات العميل والتوصيل" icon={User}>
                {addresses.length > 0 && (
                  <div className="mb-4">
                    <div className="mb-2 text-sm text-ink/60">عناوينك المحفوظة:</div>
                    <div className="flex flex-wrap gap-2">
                      {addresses.map((a) => (
                        <button key={a.id} type="button" data-testid={`use-address-${a.id}`} onClick={() => applyAddress(a)}
                          className="flex items-center gap-1.5 rounded-full border border-olive/20 bg-olive/5 px-4 py-2 text-sm text-olive-dark transition-colors hover:border-olive hover:bg-olive hover:text-ivory">
                          <MapPin className="h-3.5 w-3.5" /> {a.label} — {a.city}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="الاسم الكامل *" testid="checkout-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                  <div>
                    <label className="mb-1.5 block text-sm text-ink/60">رقم الهاتف *</label>
                    <div className="flex items-stretch gap-2" dir="ltr">
                      <span className="flex items-center rounded-xl border border-olive/20 bg-olive/5 px-3 text-sm font-semibold text-olive-dark">
                        {ARAB_COUNTRY_MAP[form.country]?.dial}
                      </span>
                      <input data-testid="checkout-phone" type="tel" inputMode="numeric" value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })}
                        placeholder="5xxxxxxxx"
                        className="w-full rounded-xl border border-olive/20 bg-white px-4 py-3 text-right outline-none focus:border-olive" dir="ltr" />
                    </div>
                    <p className="mt-1 text-[11px] text-ink/40">أدخل الرقم المحلي بدون رمز الدولة ({ARAB_COUNTRY_MAP[form.country]?.min === ARAB_COUNTRY_MAP[form.country]?.max ? `${ARAB_COUNTRY_MAP[form.country]?.min} أرقام` : `${ARAB_COUNTRY_MAP[form.country]?.min}-${ARAB_COUNTRY_MAP[form.country]?.max} أرقام`})</p>
                  </div>
                  <Field label="البريد الإلكتروني (اختياري)" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                  <div>
                    <label className="mb-1.5 block text-sm text-ink/60">الدولة *</label>
                    <select data-testid="checkout-country" value={form.country}
                      onChange={(e) => setForm({ ...form, country: e.target.value, city: "" })}
                      className="w-full rounded-xl border border-olive/20 bg-white px-4 py-3 outline-none">
                      {ARAB_COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm text-ink/60">المحافظة *</label>
                    <select data-testid="checkout-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                      className="w-full rounded-xl border border-olive/20 bg-white px-4 py-3 outline-none">
                      <option value="">اختر المحافظة...</option>
                      {(ARAB_COUNTRY_MAP[form.country]?.cities || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <Field label="المنطقة / الحي" value={form.area} onChange={(v) => setForm({ ...form, area: v })} />
                  <Field label="الشارع" value={form.street} onChange={(v) => setForm({ ...form, street: v })} />
                  <Field label="رقم البناء / المنزل" value={form.building} onChange={(v) => setForm({ ...form, building: v })} />
                  <div className="sm:col-span-2">
                    <Field label="العنوان التفصيلي *" testid="checkout-address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-sm text-ink/60">ملاحظات التوصيل (اختياري)</label>
                    <textarea data-testid="checkout-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2}
                      className="w-full rounded-xl border border-olive/20 bg-white px-4 py-3 outline-none focus:border-olive" />
                  </div>
                  {customFields.map((f) => (
                    <DynamicField key={f.key} def={f} value={customVals[f.key]} onChange={(v) => setCustomVals({ ...customVals, [f.key]: v })} />
                  ))}
                </div>
                <StepNav onNext={next} nextLabel="متابعة إلى الشحن والدفع" />
              </Card>
            )}

            {step === 2 && (
              <>
                <Card title="طريقة الشحن" icon={Truck}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Radio name="ship" checked={form.shipping_method === "standard"} onChange={() => setForm({ ...form, shipping_method: "standard" })} title="عادي" desc="يُحسب حسب الوزن والدولة" />
                    <Radio name="ship" checked={form.shipping_method === "express"} onChange={() => setForm({ ...form, shipping_method: "express" })} title="سريع" desc="توصيل أسرع" />
                  </div>
                </Card>
                <Card title="طريقة الدفع" icon={CreditCard}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Radio name="pay" testid="pay-card" checked={form.payment_method === "card"} onChange={() => setForm({ ...form, payment_method: "card" })} title="بطاقة (Stripe)" desc="دفع آمن عبر البطاقة" />
                    <Radio name="pay" testid="pay-cod" checked={form.payment_method === "cod"} onChange={() => setForm({ ...form, payment_method: "cod" })} title="الدفع عند الاستلام" desc="ادفع نقداً عند التسليم" />
                  </div>
                </Card>
                <StepNav onBack={() => setStep(1)} onNext={next} nextLabel="مراجعة الطلب" />
              </>
            )}

            {step === 3 && (
              <Card title="مراجعة الطلب قبل التأكيد" icon={ClipboardCheck}>
                <div className="space-y-3">
                  {items.map((i) => (
                    <div key={i.sku} className="flex items-center gap-3 rounded-2xl border border-olive/10 p-3">
                      <img src={i.image} alt={i.name_ar} className="h-16 w-14 shrink-0 rounded-xl object-cover" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-display text-sm font-semibold text-ink">{i.name_ar}</div>
                        <div className="text-xs text-ink/50">{fmtPrice(i.price)} × {i.qty}</div>
                      </div>
                      <div className="font-display font-bold text-olive-dark">{fmtPrice(i.price * i.qty)}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                  <ReviewMeta label="الاسم" value={form.name} />
                  <ReviewMeta label="الهاتف" value={`${ARAB_COUNTRY_MAP[form.country]?.dial || ""} ${form.phone}`.trim()} ltr />
                  <ReviewMeta label="المحافظة" value={`${form.city}${form.area ? "، " + form.area : ""}`} />
                  <ReviewMeta label="الدولة" value={ARAB_COUNTRY_MAP[form.country]?.name || form.country} />
                  <div className="col-span-2"><ReviewMeta label="العنوان" value={[form.address, form.building && `بناء ${form.building}`, form.street].filter(Boolean).join("، ")} /></div>
                  {form.note && <div className="col-span-2"><ReviewMeta label="ملاحظات" value={form.note} /></div>}
                  <ReviewMeta label="الشحن" value={form.shipping_method === "express" ? "سريع" : "عادي"} />
                  <ReviewMeta label="الدفع" value={form.payment_method === "card" ? "بطاقة" : "عند الاستلام"} />
                </div>
                <StepNav onBack={() => setStep(2)} />
              </Card>
            )}
          </div>

          <aside className="h-fit rounded-3xl border border-olive/10 bg-white p-6 lg:sticky lg:top-24">
            <h3 className="mb-4 font-display text-lg font-bold">ملخص الطلب</h3>
            <div className="mb-4 max-h-40 space-y-2 overflow-y-auto">
              {items.map((i) => (
                <div key={i.sku} className="flex justify-between text-sm text-ink/65">
                  <span>{i.name_ar} × {i.qty}</span><span>{fmtPrice(i.price * i.qty)}</span>
                </div>
              ))}
            </div>
            <div className="mb-3 flex gap-2">
              <div className="flex flex-1 items-center gap-2 rounded-xl border border-olive/20 px-3">
                <Tag className="h-4 w-4 text-olive" />
                <input data-testid="coupon-input" value={form.coupon} onChange={(e) => setForm({ ...form, coupon: e.target.value })} placeholder="كوبون (SUHOOL10)" className="w-full bg-transparent py-2.5 text-sm outline-none" />
              </div>
              <button type="button" data-testid="apply-coupon" onClick={applyCoupon} className="rounded-xl bg-olive/10 px-4 text-sm font-medium text-olive">تطبيق</button>
            </div>
            <div className="border-t border-olive/10 pt-3">
              <Row label="الإجمالي الفرعي" value={fmtPrice(subtotal)} />
              <Row label="الشحن" value={fmtPrice(shipping)} />
              {discount > 0 && <Row label="الخصم" value={`- ${fmtPrice(discount)}`} />}
              <div className="my-2 border-t border-olive/10" />
              <Row label="الإجمالي النهائي" value={fmtPrice(total)} bold />
            </div>
            {step === 3 ? (
              <button data-testid="place-order" onClick={submit} disabled={submitting}
                className="btn-shine mt-5 w-full rounded-full bg-olive py-3.5 font-semibold text-ivory transition-colors hover:bg-olive-dark disabled:opacity-60">
                {submitting ? "جارِ المعالجة..." : form.payment_method === "card" ? "تأكيد والدفع الآن" : "تأكيد الطلب"}
              </button>
            ) : (
              <button data-testid="checkout-next" onClick={next}
                className="mt-5 w-full rounded-full bg-olive py-3.5 font-semibold text-ivory transition-colors hover:bg-olive-dark">
                {step === 1 ? "متابعة" : "مراجعة الطلب"}
              </button>
            )}
            <p className="mt-3 text-center text-xs text-ink/40">يمكنك الشراء دون إنشاء حساب.</p>
          </aside>
        </div>
      </div>
    </div>
  );
}

function DynamicField({ def, value, onChange }) {
  const req = def.required ? " *" : "";
  if (def.type === "boolean") {
    return (
      <label className="flex items-center gap-2 text-sm text-ink/70">
        <input type="checkbox" data-testid={`cf-${def.key}`} checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-olive" /> {def.label}{req}
      </label>
    );
  }
  if (def.type === "select") {
    return (
      <div>
        <label className="mb-1.5 block text-sm text-ink/60">{def.label}{req}</label>
        <select data-testid={`cf-${def.key}`} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-xl border border-olive/20 bg-white px-4 py-3 outline-none">
          <option value="">اختر...</option>
          {(def.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  return <Field label={def.label + req} testid={`cf-${def.key}`} value={value || ""} onChange={onChange} type={def.type === "number" ? "number" : def.type === "date" ? "date" : "text"} />;
}

const StepNav = ({ onBack, onNext, nextLabel }) => (
  <div className="mt-6 flex items-center justify-between">
    {onBack ? (
      <button type="button" onClick={onBack} className="flex items-center gap-1.5 rounded-full border border-olive/20 px-5 py-2.5 text-sm font-medium text-ink/60">
        <ArrowRight className="h-4 w-4" /> رجوع
      </button>
    ) : <span />}
    {onNext && (
      <button type="button" data-testid="step-next" onClick={onNext}
        className="flex items-center gap-1.5 rounded-full bg-olive px-6 py-2.5 text-sm font-semibold text-ivory transition-colors hover:bg-olive-dark">
        {nextLabel} <ArrowLeft className="h-4 w-4" />
      </button>
    )}
  </div>
);

const ReviewMeta = ({ label, value, ltr }) => (
  <div className="rounded-xl bg-olive/[0.04] px-3.5 py-2.5">
    <div className="text-[11px] text-ink/45">{label}</div>
    <div className="mt-0.5 font-medium text-ink" dir={ltr ? "ltr" : "rtl"} style={ltr ? { textAlign: "left" } : {}}>{value}</div>
  </div>
);

const Card = ({ title, icon: Icon, children }) => (
  <div className="rounded-3xl border border-olive/10 bg-white p-5 sm:p-6">
    <h3 className="mb-4 flex items-center gap-2 font-display text-lg font-bold">{Icon && <Icon className="h-5 w-5 text-olive" />}{title}</h3>
    {children}
  </div>
);

const Field = ({ label, value, onChange, testid, type = "text" }) => (
  <div>
    <label className="mb-1.5 block text-sm text-ink/60">{label}</label>
    <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-olive/20 bg-white px-4 py-3 outline-none focus:border-olive" />
  </div>
);

const Radio = ({ checked, onChange, title, desc, testid }) => (
  <button type="button" data-testid={testid} onClick={onChange}
    className={`rounded-2xl border p-4 text-right transition-colors ${checked ? "border-olive bg-olive/5" : "border-olive/15 hover:border-olive/40"}`}>
    <div className="flex items-center gap-2">
      <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${checked ? "border-olive" : "border-olive/30"}`}>{checked && <span className="h-2.5 w-2.5 rounded-full bg-olive" />}</span>
      <span className="font-semibold text-ink">{title}</span>
    </div>
    <div className="mt-1 pr-7 text-sm text-ink/50">{desc}</div>
  </button>
);

const Row = ({ label, value, bold }) => (
  <div className={`flex justify-between py-1 ${bold ? "font-display text-lg font-bold text-olive-dark" : "text-sm text-ink/65"}`}>
    <span>{label}</span><span>{value}</span>
  </div>
);
